import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Copy, RotateCcw, Upload, Loader2 } from "lucide-react";
import { api, uploadImage, formatApiErrorDetail } from "../../lib/api";
import ImageUploadButton from "../../components/admin/ImageUploadButton";
import { IMAGES } from "../../data/site";

const SLOTS = [
  { key: "hero", label: "トップページ の大きな写真", def: IMAGES.hero },
  { key: "about", label: "私たちについて 上部の写真", def: IMAGES.structure },
  { key: "service", label: "事業内容 上部の写真", def: IMAGES.excavator },
  { key: "works", label: "施工実績 上部の写真", def: IMAGES.civil },
  { key: "real_estate", label: "不動産事業 上部の写真", def: IMAGES.realestate },
  { key: "company", label: "会社概要 上部の写真", def: IMAGES.facade },
  { key: "recruit", label: "採用情報 上部の写真", def: IMAGES.sunset },
  { key: "ceo", label: "代表写真（私たちについてページ）", def: "/ceo.png" },
];

export default function AdminMedia() {
  const [images, setImages] = useState({});
  const [uploads, setUploads] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [libUploading, setLibUploading] = useState(false);

  const load = () => {
    Promise.all([api.get("/admin/site-images"), api.get("/admin/uploads")])
      .then(([s, u]) => {
        setImages(s.data.images || {});
        setUploads(u.data);
        setLoaded(true);
      })
      .catch(() => toast.error("取得に失敗しました"));
  };

  useEffect(() => {
    load();
  }, []);

  const saveImages = async (next) => {
    try {
      const r = await api.put("/admin/site-images", { images: next });
      setImages(r.data.images);
      toast.success("ページの写真を変更しました");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setSlot = (key, url) => saveImages({ ...images, [key]: url });

  const resetSlot = (key) => {
    if (!window.confirm("元の写真に戻しますか？")) return;
    const next = { ...images };
    delete next[key];
    saveImages(next);
  };

  const uploadToLibrary = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLibUploading(true);
    try {
      await uploadImage(file);
      toast.success("写真をアップロードしました");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLibUploading(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard
      .writeText(`${window.location.origin}${url}`)
      .then(() => toast.success("リンクをコピーしました"))
      .catch(() => toast.error("コピーに失敗しました"));
  };

  const remove = async (u) => {
    if (!window.confirm("この写真を削除しますか？\n（施工実績などで使用中の場合は表示されなくなります）")) return;
    try {
      await api.delete(`/admin/uploads/${u.id}`);
      toast.success("削除しました");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!loaded) {
    return <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>;
  }

  return (
    <div data-testid="admin-media-page">
      <h1 className="font-mincho text-2xl font-bold text-white">写真の管理</h1>
      <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">PHOTO MANAGEMENT</p>

      <section className="mt-8 border border-line bg-panel p-6">
        <h2 className="font-mincho text-lg font-bold text-white">ページの写真</h2>
        <p className="mt-2 text-xs leading-relaxed text-gray-500">各ページで使う写真を差し替えます。「写真を選ぶ」からスマホやパソコンの写真をそのままアップロードできます。</p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SLOTS.map((slot) => (
            <div key={slot.key} data-testid={`site-image-slot-${slot.key}`} className="border border-line bg-ink">
              <div className="aspect-[16/10] w-full overflow-hidden bg-coal">
                <img src={images[slot.key] || slot.def} alt={slot.label} className="h-full w-full object-cover" />
              </div>
              <div className="p-4">
                <p className="text-sm font-bold text-white">{slot.label}</p>
                {images[slot.key] && <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-accent">変更済み</p>}
                <div className="mt-3 flex gap-2">
                  <ImageUploadButton testid={`site-image-upload-${slot.key}`} onUploaded={(url) => setSlot(slot.key, url)} />
                  {images[slot.key] && (
                    <button
                      type="button"
                      data-testid={`site-image-reset-${slot.key}`}
                      onClick={() => resetSlot(slot.key)}
                      className="flex items-center gap-1.5 border border-line px-3 py-2.5 text-xs text-gray-400 transition-colors hover:border-accent hover:text-accent"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      元に戻す
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 border border-line bg-panel p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-mincho text-lg font-bold text-white">写真ライブラリ</h2>
            <p className="mt-2 text-xs leading-relaxed text-gray-500">アップロードした写真の一覧です。施工実績・不動産物件・事業内容の編集画面からも直接アップロードできます。</p>
          </div>
          <label
            data-testid="media-upload-button"
            className={`flex cursor-pointer items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover ${libUploading ? "opacity-40" : ""}`}
          >
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadToLibrary} data-testid="media-upload-input" />
            {libUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {libUploading ? "送信中..." : "写真をアップロード"}
          </label>
        </div>
        {uploads.length === 0 ? (
          <p data-testid="media-empty" className="mt-6 text-sm text-gray-500">まだアップロードされた写真はありません。</p>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {uploads.map((u) => (
              <div key={u.id} data-testid={`media-image-${u.id}`} className="border border-line bg-ink">
                <div className="aspect-[4/3] w-full overflow-hidden bg-coal">
                  <img src={u.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div className="flex items-center justify-between gap-2 p-3">
                  <span className="font-mono text-[10px] text-gray-500">{Math.round(u.size / 1024)}KB</span>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      data-testid={`media-copy-${u.id}`}
                      onClick={() => copyUrl(u.url)}
                      title="リンクをコピー"
                      className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`media-delete-${u.id}`}
                      onClick={() => remove(u)}
                      title="削除"
                      className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
